import React from 'react';
import '../../styles/printablePlan.css';
import {
    formatTime,
    extractIngredients,
    calculateSummary
} from '../../utils/printPlanHelpers';
import {
    buildTimeline,
    computeScale,
    generateTimeTicks,
    fmt
} from '../../utils/timelineHelpers';

/**
 * SVG Icons Sprite (inline)
 */
const IconSprite = () => (
    <svg style={{ display: 'none' }}>
        <defs>
            {/* Active icon - solid square */}
            <symbol id="icon-active" viewBox="0 0 14 14">
                <rect width="14" height="14" fill="#0d1b2a" stroke="#222" strokeWidth="1"/>
            </symbol>
            {/* Inactive icon - hatched square */}
            <symbol id="icon-inactive" viewBox="0 0 14 14">
                <rect width="14" height="14" fill="#f0f0f0" stroke="#9a9a9a" strokeWidth="1"/>
                <path d="M0,0 L14,14 M14,0 L0,14" stroke="#9a9a9a" strokeWidth="1" opacity="0.3"/>
            </symbol>
            {/* Time tick icon - vertical line */}
            <symbol id="icon-tick" viewBox="0 0 2 14">
                <line x1="1" y1="0" x2="1" y2="14" stroke="#1e1e1e" strokeWidth="1"/>
            </symbol>
            {/* Conflict icon - warning square */}
            <symbol id="icon-conflict" viewBox="0 0 14 14">
                <rect width="14" height="14" fill="#b00020" stroke="#222" strokeWidth="1"/>
                <text x="7" y="11" textAnchor="middle" fill="#fff" fontSize="10" fontFamily="monospace">!</text>
            </symbol>
        </defs>
    </svg>
);

/**
 * Icon Component
 */
const Icon = ({ name, className = '' }) => (
    <svg className={`icon icon-${name} ${className}`} width="14" height="14" viewBox="0 0 14 14">
        <use href={`#icon-${name}`} />
    </svg>
);

/**
 * PrintableCookingPlan Component
 * 
 * Generates a Gantt-style timeline cooking plan with pixel-art aesthetic
 */
const PrintableCookingPlan = ({ scheduledPlan, recipes }) => {
    if (!scheduledPlan || !recipes || !Array.isArray(recipes)) {
        return (
            <div className="printable-plan-container">
                <div className="printable-plan">
                    <p>No schedule data available to print.</p>
                </div>
            </div>
        );
    }

    const { segments = [], makespanSec = 0 } = scheduledPlan;
    
    // Process data
    const timeline = buildTimeline(segments);
    const recipesWithNames = recipes.map(r => ({
        ...r,
        name: r.name || r.recipeName || r.title,
        recipeName: r.recipeName || r.name || r.title
    }));
    const ingredientsByRecipe = extractIngredients(recipesWithNames);
    const summary = calculateSummary(scheduledPlan, recipesWithNames);
    
    // Calculate timeline scale - use a reasonable base scale
    const baseScale = 2; // pixels per second
    const sPerPx = 1 / baseScale; // seconds per pixel
    
    // Minimum height for readability (small steps still need to be visible)
    const minSegmentHeight = 80; // Minimum height for any segment
    
    // Helper to calculate height based on actual time duration
    const calculateSegmentHeight = (segment) => {
        // Calculate height based on actual duration
        const timeBasedHeight = segment.durationSec * baseScale;
        // Ensure minimum height for readability, but allow longer steps to extend
        return Math.max(minSegmentHeight, timeBasedHeight);
    };
    
    // Calculate timeline scale and ticks
    const tickEvery = makespanSec > 3600 ? 600 : 300; // 10 min or 5 min intervals
    const timeTicks = generateTimeTicks(makespanSec, tickEvery);
    
    // Calculate total height needed for timeline (time-based)
    // The timeline height should be based on the total makespan
    const timelineHeight = makespanSec * baseScale + 100; // Add padding at bottom
    
    // Get ingredients for each recipe - map by recipeId from segments
    const ingredientsByRecipeId = {};
    recipesWithNames.forEach(recipe => {
        // Try multiple possible ID fields
        const recipeId = recipe.id || recipe.recipeId || recipe.recipe_id;
        if (recipeId && recipe.ingredients) {
            ingredientsByRecipeId[recipeId] = recipe.ingredients;
        }
    });
    
    // Also map by recipe name as fallback
    const ingredientsByRecipeName = {};
    recipesWithNames.forEach(recipe => {
        const recipeName = recipe.name || recipe.recipeName || recipe.title;
        if (recipeName && recipe.ingredients) {
            ingredientsByRecipeName[recipeName] = recipe.ingredients;
        }
    });
    
    // Convert lanes object to array for rendering
    const lanesArray = Object.entries(timeline.lanes).map(([recipeName, segments]) => ({
        recipeName,
        segments
    }));

    return (
        <div className="printable-plan-container">
            <IconSprite />

            <div className="printable-plan pp-wrap" style={{ '--scale': sPerPx, '--makespan': makespanSec }}>
                {/* Header */}
                <header className="pp-header">
                    <h1 className="pp-title">Menu Cooking Plan</h1>
                    
                    {/* Summary Cards */}
                    <div className="pp-grid">
                        <div className="pp-card">
                            <span className="k">Total Time</span>
                            <span className="v">{formatTime(makespanSec)}</span>
                        </div>
                        <div className="pp-card">
                            <span className="k">Active</span>
                            <span className="v">{formatTime(summary.activeTime)}</span>
                        </div>
                        <div className="pp-card">
                            <span className="k">Inactive</span>
                            <span className="v">{formatTime(summary.inactiveTime)}</span>
                        </div>
                        <div className="pp-card">
                            <span className="k">Recipes</span>
                            <span className="v">{summary.recipeCount}</span>
                        </div>
                        <div className="pp-card">
                            <span className="k">Steps</span>
                            <span className="v">{summary.totalSteps}</span>
                        </div>
                        <div className="pp-card">
                            <span className="k">Generated</span>
                            <span className="v">
                                {new Date().toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                })}
                            </span>
                        </div>
                    </div>

                    {/* Legend */}
                    <div className="pp-legend">
                        <div className="legend-item">
                            <Icon name="active" />
                            <span>Active — requires attention</span>
                        </div>
                        <div className="legend-item">
                            <Icon name="inactive" />
                            <span>Inactive — work on other recipes</span>
                        </div>
                        {timeline.conflicts.length > 0 && (
                            <div className="legend-item">
                                <Icon name="conflict" />
                                <span>{timeline.conflicts.length} conflict{timeline.conflicts.length !== 1 ? 's' : ''} detected</span>
                            </div>
                        )}
                    </div>

                    {/* Mini-map */}
                    <div className="pp-minimap">
                        {timeTicks.map((tick, idx) => (
                            <div
                                key={idx}
                                className="tick"
                                style={{ left: `${(tick.time / makespanSec) * 100}%` }}
                            />
                        ))}
                    </div>
                </header>

                {/* Conflicts Callout */}
                {timeline.conflicts.length > 0 && (
                    <div className="pp-conflicts">
                        <div className="pp-conflicts-header">
                            <Icon name="conflict" />
                            <span>Active Step Conflicts</span>
                        </div>
                        <div className="pp-conflicts-list">
                            {timeline.conflicts.map((conflict, idx) => (
                                <div key={idx} className="pp-conflict-item">
                                    <span className="pp-conflict-time">
                                        {fmt(conflict.startSec)} - {fmt(conflict.endSec)}
                                    </span>
                                    <span className="pp-conflict-recipes">
                                        {conflict.recipes.map(r => r.name).join(' + ')}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Recipe Info Cards - Horizontally Stacked Above Timeline */}
                <section className="pp-recipes-header">
                    <div className="pp-recipes-header-container" style={{ '--recipe-count': lanesArray.length }}>
                        {/* Spacer for time ruler */}
                        <div className="pp-recipes-header-spacer"></div>
                        
                        {/* Recipe Info Cards */}
                        <div 
                            className="pp-recipes-header-cards"
                            style={{ 
                                display: 'grid',
                                gridTemplateColumns: `repeat(${lanesArray.length}, 1fr)`,
                                gap: '8px',
                                width: '100%',
                                flex: 1
                            }}
                        >
                            {lanesArray.map((lane, laneIdx) => {
                                // Get ingredients for this recipe
                                const recipeId = lane.segments[0]?.recipeId || lane.segments[0]?.recipe_id;
                                const recipeIngredients = (recipeId && ingredientsByRecipeId[recipeId]) 
                                    || ingredientsByRecipeName[lane.recipeName] 
                                    || null;
                                
                                return (
                                    <div 
                                        key={laneIdx} 
                                        className="pp-recipe-header-card"
                                        style={{ 
                                            display: 'block',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        <div className="pp-recipe-header-name">{lane.recipeName}</div>
                                        {recipeIngredients && recipeIngredients.length > 0 && (
                                            <div className="pp-recipe-header-ingredients">
                                                <div className="pp-recipe-header-ingredients-label">Ingredients:</div>
                                                <ul className="pp-recipe-header-ingredients-list">
                                                    {recipeIngredients.map((ingredient, ingIdx) => {
                                                        const ingredientText = typeof ingredient === 'string' 
                                                            ? ingredient 
                                                            : ingredient.name || ingredient;
                                                        return (
                                                            <li key={ingIdx}>{ingredientText}</li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Timeline Schedule - Vertical Layout */}
                <section className="pp-schedule">
                    <div className="pp-schedule-container" style={{ '--recipe-count': lanesArray.length }}>
                        {/* Time Ruler - Vertical on left */}
                        <div className="pp-time-ruler-vertical" style={{ height: `${timelineHeight}px` }}>
                            {timeTicks.map((tick, idx) => (
                                <div
                                    key={idx}
                                    className="pp-ruler-tick-vertical"
                                    style={{ top: `${tick.time * baseScale}px` }}
                                >
                                    <div className="pp-ruler-tick-line-vertical"></div>
                                    <div className="pp-ruler-tick-label-vertical">{tick.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Recipe Columns - Timeline Only */}
                        <div className="pp-columns">
                            {lanesArray.map((lane, laneIdx) => {
                                return (
                                    <div key={laneIdx} className="pp-column">
                                        {/* Timeline Grid */}
                                        <div className="pp-column-grid" style={{ height: `${timelineHeight}px`, position: 'relative' }}>
                                            {lane.segments.map((segment, segIdx) => {
                                                // Time-based positioning with dynamic height
                                                const topPx = segment.startSec * baseScale;
                                                const heightPx = calculateSegmentHeight(segment);
                                                
                                                // Track active step index for border color variation
                                                const activeStepIndex = segment.type === 'active' 
                                                    ? (segment.stepIndex !== undefined ? segment.stepIndex : segIdx)
                                                    : null;
                                                
                                                return (
                                                    <div
                                                        key={segIdx}
                                                        className={`pp-segment-vertical ${segment.type === 'active' ? 'pp-segment-active' : ''}`}
                                                        data-type={segment.type}
                                                        {...(activeStepIndex !== null ? { 'data-step-index': activeStepIndex } : {})}
                                                        style={{
                                                            position: 'absolute',
                                                            top: `${topPx}px`,
                                                            height: `${heightPx}px`,
                                                            left: '8px',
                                                            right: '8px',
                                                            zIndex: segment.type === 'active' ? 3 : 2
                                                        }}
                                                    >
                                                        <div className="pp-segment-vertical-content">
                                                            <div className="pp-segment-vertical-header">
                                                                <span className="pp-segment-vertical-step">
                                                                    Step {segment.stepIndex !== undefined ? segment.stepIndex + 1 : '?'}
                                                                </span>
                                                                <span className="pp-segment-vertical-type">
                                                                    {segment.type === 'active' ? 'ACTIVE' : 'INACTIVE'}
                                                                </span>
                                                            </div>
                                                            <div className="pp-segment-vertical-name">
                                                                {segment.stepTitle || segment.label || 'No description'}
                                                            </div>
                                                            <div className="pp-segment-vertical-time">
                                                                <span>{fmt(segment.startSec)} - {fmt(segment.endSec)}</span>
                                                                <span>•</span>
                                                                <span>{formatTime(segment.durationSec)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            
                                            {/* Time markers overlay for reference */}
                                            <div className="pp-column-time-markers" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '100%', pointerEvents: 'none', zIndex: 1 }}>
                                                {timeTicks.map((tick, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="pp-time-marker-line"
                                                        style={{
                                                            position: 'absolute',
                                                            top: `${tick.time * baseScale}px`,
                                                            left: 0,
                                                            right: 0,
                                                            height: '1px',
                                                            background: 'rgba(0, 0, 0, 0.05)',
                                                            borderTop: '1px dashed rgba(0, 0, 0, 0.1)'
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Tips Section */}
                <section className="pp-tips page-break-before">
                    <h3>Cooking Tips</h3>
                    <ul className="pp-tips-list">
                        <li>Read through the entire plan before starting</li>
                        <li>Prepare all ingredients before you begin (mise en place)</li>
                        <li>Active steps require your full attention — inactive steps allow you to work on other recipes</li>
                        <li>Set timers for inactive periods to stay on track</li>
                        <li>Clean as you go to keep your workspace organized</li>
                        <li>Keep a copy of this plan nearby for quick reference</li>
                    </ul>
                </section>

                {/* Footer */}
                <footer className="pp-footer">
                    Generated by 4RKED Recipe System · Menu Planning Feature
                </footer>
            </div>
        </div>
    );
};

export default PrintableCookingPlan;
